import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { RoundToTenthPipe } from '../../../../core/pipes/round-to-tenth.pipe';

interface PriceEvaluation {
  status: string;
  marketComparison: string;
  isGoodDeal: boolean;
}

interface LocationEvaluation {
  rating: number;
  pros: string[];
  description: string;
}

interface AIAnalysis {
  priceEvaluation?: PriceEvaluation;
  locationEvaluation?: LocationEvaluation;
  summary?: string;
  smartInsights?: string[];
}

@Component({
  selector: 'app-ai-analysis',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RoundToTenthPipe],
  template: `
    <div class="surface-card relative overflow-hidden p-4 sm:p-8">
      <div class="absolute -right-10 -top-10 w-32 h-32 bg-primary/20 rounded-full blur-2xl pointer-events-none"></div>
      <div class="absolute -left-10 -bottom-10 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none"></div>

      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border-subtle pb-4 mb-6">
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center text-primary shadow-glow">
            <i class="fa-solid fa-wand-magic-sparkles text-sm animate-pulse"></i>
          </div>
          <div class="min-w-0">
            <h2 class="caption font-black uppercase tracking-widest text-primary font-mono">
              HomiGo Intelligence
            </h2>
            <h3 class="text-lg sm:text-xl md:text-2xl font-medium text-text-primary mt-0.5 break-words">
              {{ 'unitDetails.ai.title' | translate }}
            </h3>
          </div>
        </div>
        <span class="badge badge-primary font-mono self-start sm:self-auto">
          Live Core v2
        </span>
      </div>

      @if (aiAnalysis) {
        <div class="space-y-5">
          @if (aiAnalysis.priceEvaluation) {
            <div class="group surface-elevated bg-bg-secondary p-3 sm:p-4 hover:border-primary/50 transition-all duration-300 flex gap-3 sm:gap-4 min-w-0">
              <div class="w-8 h-8 rounded-xl bg-success/10 text-success flex items-center justify-center border border-success/20 shrink-0 shadow-sm">
                <i class="fa-solid fa-scale-balanced text-xs"></i>
              </div>
              <div class="space-y-1 min-w-0">
                <div class="text-xs sm:text-sm font-extrabold text-text-primary flex flex-wrap items-center gap-2">
                  <span>{{ aiAnalysis.priceEvaluation.status }}</span>
                  @if (aiAnalysis.priceEvaluation.isGoodDeal) {
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-[10px] font-black rounded-md border border-success/20 uppercase tracking-wide">
                      <i class="fa-solid fa-fire text-[9px]"></i> Good Deal
                    </span>
                  }
                </div>
                <p class="body-small leading-relaxed">
                  {{ aiAnalysis.priceEvaluation.marketComparison }}
                </p>
              </div>
            </div>
          }

          @if (aiAnalysis.locationEvaluation) {
            <div class="group surface-elevated bg-bg-secondary p-3 sm:p-4 hover:border-primary/50 transition-all duration-300 flex gap-3 sm:gap-4 min-w-0">
              <div class="w-8 h-8 rounded-xl bg-info/10 text-info flex items-center justify-center border border-info/20 shrink-0 shadow-sm">
                <i class="fa-solid fa-map-location-dot text-xs"></i>
              </div>
              <div class="space-y-1 flex-1 min-w-0">
                <div class="text-xs sm:text-sm font-extrabold text-text-primary flex flex-col min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between gap-2">
                  <span>{{ 'unitDetails.ai.locationScore' | translate }}</span>
                  @if (aiAnalysis.locationEvaluation.rating > 0) {
                    <span class="font-mono text-xs font-black px-2 py-0.5 bg-warning/10 text-warning border border-warning/20 rounded-md">
                      {{ aiAnalysis.locationEvaluation.rating | roundToTenth }} {{ 'unitDetails.ai.outOf10' | translate }}
                    </span>
                  }
                </div>
                <p class="body-small leading-relaxed">
                  {{ aiAnalysis.locationEvaluation.description }}
                </p>

                @if (aiAnalysis.locationEvaluation.pros && aiAnalysis.locationEvaluation.pros.length) {
                  <div class="flex flex-wrap gap-1.5 pt-2">
                    @for (pro of aiAnalysis.locationEvaluation.pros; track pro) {
                      <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-surface border border-border rounded-md text-text-secondary text-[11px] font-bold">
                        <i class="fa-solid fa-circle-check text-success text-[10px]"></i>
                        {{ pro }}
                      </span>
                    }
                  </div>
                }
              </div>
            </div>
          }

          @if (aiAnalysis.summary) {
            <div class="group surface-elevated bg-bg-secondary p-3 sm:p-4 hover:border-primary/50 transition-all duration-300 flex gap-3 sm:gap-4 min-w-0">
              <div class="w-8 h-8 rounded-xl bg-warning/10 text-warning flex items-center justify-center border border-warning/20 shrink-0 shadow-sm">
                <i class="fa-solid fa-file-invoice text-xs"></i>
              </div>
              <div class="space-y-1 min-w-0">
                <div class="text-xs sm:text-sm font-extrabold text-text-primary">
                  {{ 'unitDetails.ai.marketComparison' | translate }}
                </div>
                <p class="body-small leading-relaxed">
                  {{ aiAnalysis.summary }}
                </p>
              </div>
            </div>
          }

          @if (aiAnalysis.smartInsights && aiAnalysis.smartInsights.length) {
            <div class="group surface-elevated bg-bg-secondary p-3 sm:p-4 hover:border-primary/50 transition-all duration-300 flex gap-3 sm:gap-4 min-w-0">
              <div class="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shrink-0 shadow-sm">
                <i class="fa-solid fa-brain text-xs"></i>
              </div>
              <div class="flex-1 space-y-2">
                <div class="text-xs sm:text-sm font-extrabold text-text-primary">
                  {{ 'unitDetails.ai.topFeatures' | translate }}
                </div>
                <ul class="space-y-1.5">
                  @for (insight of aiAnalysis.smartInsights; track insight) {
                    <li class="body-small flex items-start gap-2">
                      <i class="fa-solid fa-bolt text-primary text-[10px] mt-1 shrink-0"></i>
                      <span class="leading-relaxed">{{ insight }}</span>
                    </li>
                  }
                </ul>
              </div>
            </div>
          }
        </div>

      } @else {
        <div class="space-y-4">
          <div class="surface-elevated bg-bg-secondary p-3 sm:p-4 flex gap-3 sm:gap-4 min-w-0">
            <div class="w-8 h-8 rounded-xl bg-success/10 text-success flex items-center justify-center border border-success/20 shrink-0">
              <i class="fa-solid fa-arrow-trend-down text-xs"></i>
            </div>
            <div class="min-w-0">
              <div class="text-xs sm:text-sm font-extrabold text-text-primary">Fair Price Match</div>
              <p class="body-small mt-0.5 leading-relaxed">
                Price is 8% below market average for similar units in the same area.
              </p>
            </div>
          </div>

          <div class="surface-elevated bg-bg-secondary p-3 sm:p-4 flex gap-3 sm:gap-4 min-w-0">
            <div class="w-8 h-8 rounded-xl bg-info/10 text-info flex items-center justify-center border border-info/20 shrink-0">
              <i class="fa-solid fa-compass text-xs"></i>
            </div>
            <div class="min-w-0">
              <div class="text-xs sm:text-sm font-extrabold text-text-primary">
                Excellent Location Network
              </div>
              <p class="body-small mt-0.5 leading-relaxed">
                Close to schools, malls, and public transit nodes. Location rating: 9/10.
              </p>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class AiAnalysisComponent {
  @Input() aiAnalysis?: AIAnalysis;
}



